/**
 * The browser side of passkeys (v11.0.0).
 *
 * Pure conversions between the Worker's JSON and the WebAuthn API's binary
 * shapes, plus the words for each way a ceremony can end. No library: there is
 * no security decision on this side — the Worker checks everything — and
 * `PublicKeyCredential.parseCreationOptionsFromJSON` / `toJSON()` are too new
 * to rely on across the iOS versions in use, so the few lines are written out.
 *
 * ONE RULE the callers must keep (WebKit): call `navigator.credentials.create`
 * or `.get` as the FIRST await after the tap. Safari ties the passkey sheet to
 * the user's tap, and a network round trip before it can spend that tap — so
 * the options are fetched BEFORE the tap that uses them (docs/SECURITY.md §14).
 */

import { ApiError } from './api';

export function bytesToB64u(bytes: ArrayBuffer | Uint8Array): string {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64uToBytes(value: string): Uint8Array<ArrayBuffer> {
  const n = value.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(n + '='.repeat((4 - (n.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** What the Worker sends (SimpleWebAuthn's JSON shapes, only the fields used). */
export interface CreationOptionsJSON {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
  authenticatorSelection?: {
    residentKey?: string;
    requireResidentKey?: boolean;
    userVerification?: string;
    authenticatorAttachment?: string;
  };
  attestation?: string;
}

export interface RequestOptionsJSON {
  challenge: string;
  rpId?: string;
  timeout?: number;
  allowCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
  userVerification?: string;
}

export function creationOptions(json: CreationOptionsJSON): CredentialCreationOptions {
  return {
    publicKey: {
      challenge: b64uToBytes(json.challenge),
      rp: json.rp,
      user: { ...json.user, id: b64uToBytes(json.user.id) },
      pubKeyCredParams: json.pubKeyCredParams,
      timeout: json.timeout,
      excludeCredentials: (json.excludeCredentials ?? []).map((c) => ({
        type: 'public-key' as const,
        id: b64uToBytes(c.id),
        transports: c.transports as AuthenticatorTransport[] | undefined,
      })),
      authenticatorSelection: json.authenticatorSelection as AuthenticatorSelectionCriteria,
      attestation: (json.attestation ?? 'none') as AttestationConveyancePreference,
    },
  };
}

export function requestOptions(json: RequestOptionsJSON): CredentialRequestOptions {
  return {
    publicKey: {
      challenge: b64uToBytes(json.challenge),
      rpId: json.rpId,
      timeout: json.timeout,
      allowCredentials: (json.allowCredentials ?? []).map((c) => ({
        type: 'public-key' as const,
        id: b64uToBytes(c.id),
        transports: c.transports as AuthenticatorTransport[] | undefined,
      })),
      userVerification: (json.userVerification ?? 'required') as UserVerificationRequirement,
    },
  };
}

/** The fields of a PublicKeyCredential this app reads — a structural type, so a test double fits. */
export interface CredentialLike {
  id: string;
  type: string;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject?: ArrayBuffer;
    getTransports?: () => string[];
    authenticatorData?: ArrayBuffer;
    signature?: ArrayBuffer;
    userHandle?: ArrayBuffer | null;
  };
}

export function registrationJSON(cred: CredentialLike) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: cred.id,
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      attestationObject: bytesToB64u(r.attestationObject!),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : undefined,
    },
  };
}

export function authenticationJSON(cred: CredentialLike) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: cred.id,
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      authenticatorData: bytesToB64u(r.authenticatorData!),
      signature: bytesToB64u(r.signature!),
      userHandle: r.userHandle ? bytesToB64u(r.userHandle) : null,
    },
  };
}

/** Whether this browser can do WebAuthn at all. */
export function passkeysSupported(w: {
  PublicKeyCredential?: unknown;
  navigator?: { credentials?: { create?: unknown; get?: unknown } };
}): boolean {
  return (
    typeof w.PublicKeyCredential === 'function' &&
    typeof w.navigator?.credentials?.create === 'function' &&
    typeof w.navigator?.credentials?.get === 'function'
  );
}

/** A challenge is good for five minutes on the Worker; refresh well inside that. */
export const SIGN_IN_OPTIONS_MAX_AGE_MS = 4 * 60 * 1000;

/**
 * Words for a passkey sign-in that did not finish. Null means say nothing:
 * the person closed the sheet themselves, which is not an error.
 */
export function passkeySignInErrorText(err: unknown): string | null {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many attempts. Wait a few minutes, then try again.';
    if (err.status === 401) return err.message;
    if (err.status === 404)
      return 'Passkeys are not available at this address. Sign in with your password.';
    return 'Ballast could not check that passkey. Try again, or sign in with your password.';
  }
  const name = err instanceof Error ? err.name : '';
  // NotAllowedError is also what a cancelled sheet and a timeout look like —
  // and, on some platforms, "no passkey for this site".
  if (name === 'NotAllowedError' || name === 'AbortError') return null;
  if (name === 'SecurityError')
    return 'Passkeys are not available at this address. Sign in with your password.';
  if (err instanceof TypeError) return "Couldn't reach Ballast. Check your connection.";
  return "That passkey didn't work. Sign in with your password.";
}

/** Words for an enrolment that did not finish (the sheet stays open). */
export function passkeyEnrolErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'That password did not match.';
    if (err.status === 429) return 'Too many attempts. Wait a few minutes, then try again.';
    if (err.status === 503) return 'Ballast cannot check your password right now.';
    return err.message;
  }
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'No passkey was saved. Tap Save passkey to try again.';
  }
  if (name === 'InvalidStateError')
    return 'Your passkey app already holds a Ballast passkey for this account.';
  if (name === 'SecurityError') return 'Passkeys are not available at this address.';
  if (err instanceof TypeError) return "Couldn't reach Ballast. Check your connection.";
  return 'That passkey could not be saved. Try again.';
}

/** "Added 11 Oct 2026 · last used 3 days ago" — the line under each passkey. */
export function passkeyMeta(
  p: { createdAt: string; lastUsedAt: string | null },
  now: Date,
): string {
  const added = new Date(p.createdAt).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (!p.lastUsedAt) return `Added ${added} · not used yet`;
  const days = Math.floor((now.getTime() - new Date(p.lastUsedAt).getTime()) / 86_400_000);
  const used = days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
  return `Added ${added} · last used ${used}`;
}
